import { FEATURE_KEYS } from '@/types';
import type { Plan, PlanInput } from '@/types/platform';
import { ServiceError } from '../../contracts';
import type { PlatformPlanService } from '../../platformContracts';
import { adminRoot, delay, pctx, platformRoot, recordAudit, savePlatform, syncAllTenantsOnPlan, uid } from './context';

const invalid = (m: string) => new ServiceError(m, 'INVALID');

function tenantCount(planId: string) {
  return Object.values(adminRoot()).filter((t) => t.planId === planId).length;
}

type PlatformStoreLike = ReturnType<typeof platformRoot>;
function validate(d: PlatformStoreLike, input: PlanInput, selfId?: string) {
  if (!input.name.trim()) throw invalid('Plan name is required');
  if (d.plans.some((p) => p.id !== selfId && p.name.toLowerCase() === input.name.trim().toLowerCase())) throw new ServiceError('Another plan already uses this name', 'CONFLICT');
  if (input.priceMonthly < 0 || input.priceYearly < 0) throw invalid('Prices cannot be negative');
  if (input.limits.products < 1 || input.limits.staff < 1 || input.limits.ordersPerMonth < 1) throw invalid('Limits must be at least 1');
  FEATURE_KEYS.forEach((k) => { if (typeof input.features[k] !== 'boolean') throw invalid('Every feature must be explicitly on or off'); });
}

export const platformPlans: PlatformPlanService = {
  async list() {
    pctx('platform.plans.manage');
    const d = platformRoot();
    return delay(d.plans.map((p) => ({ ...p, tenantCount: tenantCount(p.id) })).sort((a, b) => a.priceMonthly - b.priceMonthly));
  },
  async create(input) {
    const { actor } = pctx('platform.plans.manage');
    const d = platformRoot();
    validate(d, input);
    const now = new Date().toISOString();
    const plan: Plan = { ...input, id: uid('plan'), createdAt: now, updatedAt: now };
    d.plans.push(plan);
    savePlatform();
    recordAudit(actor, 'plan.created', 'PLAN', plan.id, plan.name, `Created plan "${plan.name}" (₹${plan.priceMonthly}/mo)`);
    return delay(plan, 500);
  },
  async update(id, input) {
    const { actor } = pctx('platform.plans.manage');
    const d = platformRoot();
    const i = d.plans.findIndex((p) => p.id === id);
    if (i < 0) throw new ServiceError('Plan not found', 'NOT_FOUND');
    validate(d, input, id);
    const before = d.plans[i];
    const changedFeatures = FEATURE_KEYS.filter((k) => before.features[k] !== input.features[k]);
    d.plans[i] = { ...before, ...input, id, updatedAt: new Date().toISOString() };
    savePlatform();
    syncAllTenantsOnPlan(id); // every tenant on this plan (without an override for a changed key) sees the new default immediately
    const affected = tenantCount(id);
    recordAudit(actor, 'plan.updated', 'PLAN', id, d.plans[i].name, changedFeatures.length ? `Changed ${changedFeatures.join(', ')} — resynced ${affected} tenant${affected === 1 ? '' : 's'}` : 'Updated plan details');
    return delay(d.plans[i], 500);
  },
  async setActive(id, active) {
    const { actor } = pctx('platform.plans.manage');
    const d = platformRoot();
    const p = d.plans.find((x) => x.id === id);
    if (!p) throw new ServiceError('Plan not found', 'NOT_FOUND');
    p.active = active;
    p.updatedAt = new Date().toISOString();
    savePlatform();
    recordAudit(actor, 'plan.availability_changed', 'PLAN', id, p.name, active ? 'Made available for new stores' : 'Hidden from new stores (existing tenants keep it)');
    return delay(p, 300);
  },
  async remove(id) {
    const { actor } = pctx('platform.plans.manage');
    const d = platformRoot();
    const p = d.plans.find((x) => x.id === id);
    if (!p) throw new ServiceError('Plan not found', 'NOT_FOUND');
    const n = tenantCount(id);
    if (n > 0) throw new ServiceError(`${n} store${n === 1 ? ' is' : 's are'} on this plan. Move them to another plan first.`, 'CONFLICT');
    d.plans = d.plans.filter((x) => x.id !== id);
    savePlatform();
    recordAudit(actor, 'plan.deleted', 'PLAN', id, p.name, `Deleted plan "${p.name}"`);
    return delay(undefined, 300);
  },
};
